'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { useKeyboardArbitrationCandidate } from '#pplx/frontend/composer-sdk/internal/keyboard/keyboardArbitration.js';
import { isKeyboardCommandEligible, notifyKeyboardUserEdit, resolveKeyboardConfigurations, } from '#pplx/frontend/composer-sdk/internal/keyboard/keyboardCapabilityUtils.js';
import { UserEditPlugin } from '#pplx/frontend/composer-sdk/internal/UserEditPlugin.js';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, COMMAND_PRIORITY_EDITOR, KEY_ESCAPE_COMMAND } from 'lexical';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
function toComposerKeyboardEvent(event, key, defaultPrevented) {
    return {
        altKey: event.altKey,
        code: event.code,
        ctrlKey: event.ctrlKey,
        defaultPrevented,
        key,
        metaKey: event.metaKey,
        repeat: event.repeat,
        shiftKey: event.shiftKey,
    };
}
function replaceValue(editor, value) {
    const editorState = editor.parseEditorState(value, () => $getRoot().selectEnd());
    editor.setEditorState(editorState);
}
function runConfigurations(event, configurations, editor, key, defaultPrevented) {
    const enabledConfigurations = configurations.filter((configuration) => configuration.disabled !== true);
    if (enabledConfigurations.length === 0)
        return false;
    let serializedValue;
    const context = {
        get value() {
            serializedValue ??= editor.getEditorState().toJSON();
            return serializedValue;
        },
        replaceValue: (value) => replaceValue(editor, value),
    };
    const keyboardEvent = toComposerKeyboardEvent(event, key, defaultPrevented);
    const handled = enabledConfigurations.some((configuration) => configuration.onKeyDown(keyboardEvent, context));
    return handled;
}
function runEscapeConfigurations(event, configurations, editor) {
    if (editor.isComposing() || !isKeyboardCommandEligible(event))
        return false;
    const handled = runConfigurations({
        altKey: event.altKey,
        code: event.code,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
        repeat: event.repeat,
        shiftKey: event.shiftKey,
    }, configurations, editor, 'Escape', event.defaultPrevented);
    if (handled)
        event.preventDefault();
    return handled;
}
/** Mounts plugin keyboard behavior inside Lexical's command pipeline. */
export function KeyboardCapabilityHost({ plugins, onUserEdit, }) {
    const [editor] = useLexicalComposerContext();
    const configurations = useMemo(() => resolveKeyboardConfigurations(plugins), [plugins]);
    const configurationsRef = useRef(configurations);
    configurationsRef.current = configurations;
    const handleUserEdit = useCallback(() => {
        onUserEdit();
        notifyKeyboardUserEdit(configurationsRef.current);
    }, [onUserEdit]);
    useKeyboardArbitrationCandidate({
        priority: 0,
        onKeyDown: (event, key) => runConfigurations(event, configurationsRef.current, editor, key, false),
    });
    // Equal-priority Lexical handlers run in registration order.
    // Escape stays outside arrow-key arbitration so Lexical overlays can handle it before keyboard plugins.
    useLayoutEffect(() => {
        return editor.registerCommand(KEY_ESCAPE_COMMAND, (event) => runEscapeConfigurations(event, configurationsRef.current, editor), COMMAND_PRIORITY_EDITOR);
    }, [editor]);
    return _jsx(UserEditPlugin, { onUserEdit: handleUserEdit });
}
//# sourceMappingURL=KeyboardCapabilityHost.js.map